import { useEffect, useRef } from 'react';

const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max);

/**
 * Slides a band's seam from one position to another as it scrolls into view.
 *
 * Every band declares where its seam starts and where it ends, and one band's
 * end is the next band's start — so at the moment a boundary is on screen both
 * sides read the same value and the seam looks like one continuous line
 * migrating down the page rather than a set of steps.
 *
 * Progress is measured from the band's top edge alone: 0 when it is at the
 * bottom of the viewport, 1 when it reaches the top. The seam therefore
 * finishes moving as the band settles into place, and holds still while it is
 * being read.
 *
 * Written to fail toward the finished position: with no JavaScript, no
 * matchMedia, reduced motion, or a viewport too narrow for a two-sided layout,
 * `--seam` is never set and CSS falls back to `--seam-to`.
 */
export function useSeam<T extends HTMLElement = HTMLElement>(from: number, to: number) {
  const ref = useRef<T>(null);

  useEffect(() => {
    const element = ref.current;
    if (!element || typeof window.matchMedia !== 'function') return;

    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
    const stacked = window.matchMedia('(max-width: 1023.98px)');
    let frame = 0;

    const apply = () => {
      frame = 0;

      // Stacked, or motion turned down: let the stylesheet hold the end state.
      if (reduced.matches || stacked.matches) {
        element.style.removeProperty('--seam');
        return;
      }

      const { top } = element.getBoundingClientRect();
      const progress = clamp((window.innerHeight - top) / window.innerHeight, 0, 1);
      element.style.setProperty('--seam', `${(from + (to - from) * progress).toFixed(3)}%`);
    };

    const schedule = () => {
      if (!frame) frame = requestAnimationFrame(apply);
    };

    apply();
    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule);
    reduced.addEventListener('change', apply);
    stacked.addEventListener('change', apply);

    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener('scroll', schedule);
      window.removeEventListener('resize', schedule);
      reduced.removeEventListener('change', apply);
      stacked.removeEventListener('change', apply);
    };
  }, [from, to]);

  return ref;
}
